import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import moment from "moment";
import { isObject } from "lodash";
import { TripType } from "../../../../pages/objects/tripType";
import { MinimalStation } from "../../../../Itinerary/objects/minimalStation";
import { MinimalIataAirport } from "../../../../Itinerary/objects/minimalIataAirport";
import { PackageLowestPrice } from "../../../../pages/objects/packageLowestPrice";
import { Circuit } from "../../../../Itinerary/objects/circuit";
import { CircuitExtraFields } from "../../../../pages/utils/makeCircuitsRequest";
import { Trip } from "../../../MaterialTripList/objects/trip";
import { ContractData } from "../../../../Itinerary/objects/contractData";

export const packagesApi = createApi({
    reducerPath: 'api.packages',
    baseQuery: axiosBaseQuery({
        baseUrl: (queryParameters) => {
            if (queryParameters.v2) {
                return `${API_HREF}client/${window.id_owner}`.replace('v1', 'v2');
            }
            return `${API_HREF}client/${window.id_owner}`;
        }
    }),
    tagTypes: [
        'destination-inheritance',
        'packages-lowest-price',
        'itinerary-types',
        'contract-data'
    ],
    endpoints: (builder) => ({
        getContractData: builder.query<
            ContractData | null,
            {
                circuitId: number,
                contractId: number
            }
        >({
            query: (options) => {
                return {
                    url: `/circuits/${options.circuitId}/contracts/${options.contractId}/contract-data/`,
                }
            },
            transformResponse(data: { results: ContractData[] }) {
                return data.results[0] ?? null;
            },
            providesTags: ['contract-data']
        }),
        getDestinationInheritance: builder.query<
            string[],
            {
                destinationId?: number | null
            }
        >({
            query: (options) => {
                return {
                    url: `/destinations/${options.destinationId}/get_inheritance/`,
                }
            },
            transformResponse(data: string) {
                return data.split(',');
            },
            providesTags: ['destination-inheritance']
        }),
        getPackagesByDestination: builder.query<
            (Circuit & CircuitExtraFields)[],
            {
                destinations: string[] | undefined,
                destinationId?: number | null,
                nightsCount?: number | null,
                departureDate?: string | null,
                airportId?: number | null,
                search?: string | null,
                category?: number | null,
                isForSearchbar?: boolean,
                noDestination?: boolean,
                quotationCode: string
            }
        >({
            query: (options) => {
                return {
                    method: 'POST',
                    url: '/circuits/cms_by_destination/',
                    data: {
                        destination_list: options.destinations,
                    },
                    params: {
                        cms_reduced: !options.isForSearchbar ? true : undefined,
                        visibility__in: 'PUBLIC,PRIVATE_TO',
                        stack_product_price__start_date: options.departureDate ?
                            moment.utc(options.departureDate).toISOString() :
                            undefined,
                        departure_airport: options.airportId,
                        category_ids: options.category,
                        include_destination_info: options.noDestination,
                        search: options.search,
                        cms_search_bar: options.isForSearchbar ?? undefined,
                        has_enabled_process: true,
                        dont_check_bo_param_departures_for_combine: options.quotationCode === 'beachcomber' ?
                            options.quotationCode :
                            undefined
                    },
                    v2: true
                }
            },
            providesTags: ['packages-lowest-price']
        }),
        getPackagesLowestPrice: builder.query<
            {
                available_stations: MinimalStation[],
                available_airports: MinimalIataAirport[],
                available_durations: number[],
                available_categories: TripType[],
                versions_lowest_prices: PackageLowestPrice[],
                nb_of_islands_included: (number | null)[],
                filtered_date_prices: {
                    id: number,
                    parent_id: number,
                    start_date: string,
                    end_date: string,
                    prices_terrestrial__price: number | null,
                    prices_flight__price: number | null,
                    trip_version: number
                }[]
            },
            {
                destinations: string[] | null,
                nightsCount: number | null,
                departureDate: string | null,
                airportId: number | null,
                search: string | null,
                category: number | null,
                quotationCode: string
            }
        >({
            query: (options) => {
                return {
                    method: 'POST',
                    url: '/circuits/lowest_price_by_destination/',
                    data: {
                        destination_list: options.destinations ?? undefined,
                    },
                    params: {
                        cms_reduced: true,
                        visibility__in: 'PUBLIC,PRIVATE_TO',
                        // duration_night: options.nightsCount,
                        stack_product_price__start_date: options.departureDate ?
                            moment.utc(options.departureDate).toISOString() :
                            undefined,
                        departure_airport: options.airportId ?? undefined,
                        categories__id: options.category ?? undefined,
                        search: options.search ?? undefined,
                        has_enabled_process: true,
                        dont_check_bo_param_departures_for_combine: options.quotationCode === 'beachcomber' ?
                            options.quotationCode :
                            undefined
                    }
                }
            },
            providesTags: ['packages-lowest-price']
        }),
        getItineraryTypesByDestination: builder.query<
            Trip[],
            {
                destinations: string[] | undefined,
                destinationId?: number | null,
                nightsCount?: number | null,
                departureDate?: string | null,
                airportId?: number | null,
                search?: string | null,
                category?: number | null
            }
        >({
            query: (options) => {
                if (options.destinationId) {
                    return {
                        method: 'POST',
                        url: '/trip/by_destination/',
                        data: {
                            destination_list: options.destinations,
                        },
                        params: {
                            cms_reduced: true,
                            typical: 1,
                            visibility__in: 'PUBLIC,PRIVATE_TO',
                            homepage: true,
                            duration_night: options.nightsCount,
                            stack_product_price__start_date: options.departureDate ?
                                moment.utc(options.departureDate).toISOString() :
                                undefined,
                            departure_airport: options.airportId,
                            categories__id: options.category,
                            has_enabled_process: true
                        }
                    };
                }
                return {
                    url: '/trip/',
                    params: {
                        cms_reduced: true,
                        typical: 1,
                        visibility__in: 'PUBLIC,PRIVATE_TO',
                        homepage: true,
                        duration_night: options.nightsCount,
                        stack_product_price__start_date__exact: options.departureDate ?
                            moment.utc(options.departureDate).toISOString() :
                            undefined,
                        departure_airport: options.airportId,
                        categories__id: options.category,
                        search: options.search
                    }
                };
            },
            transformResponse(data) {
                if (isObject(data) && 'results' in data) {
                    return data.results as Trip[];
                }
                return data as Trip[];
            },
            providesTags: ['itinerary-types']
        }),
    })
});

export const {
    useGetContractDataQuery,
    useGetDestinationInheritanceQuery,
    useGetPackagesByDestinationQuery,
    useGetPackagesLowestPriceQuery,
    useGetItineraryTypesByDestinationQuery
} = packagesApi;